/**
 * useCapturaHydrate — hidratar el formulario de Captura desde el servidor
 * cuando el técnico abre una parcela que ya tiene captura previa.
 *
 * Política de hidratación honesta:
 *   • Datos legibles directo de DB (no PII): tipo de tenencia, folios,
 *     clave catastral, titular_masked (solo display), todos los campos
 *     de `gestiones` (estado, riesgo, plazo, monto_renta_ha) y el JSON
 *     parseado de `gestiones.notas` (sub-bloques contrato/gestion/campo).
 *   • Irregularidades: subtipo + gravedad + descripción.
 *   • PII cifrada (`*_enc` en `propietarios`: titular, telefono, email,
 *     curp, rfc, clabe, direccion): NO se hidratan aquí. La descifra
 *     vive en el BFF con MFA reciente como prerequisito; cuando se
 *     habilite, se sumará un segundo hook `useCapturaHydrateUnmasked`.
 *
 * El consumidor (CapturaPage) decide si aplicar la hidratación. La regla
 * actual: aplicar solo si el state controlado del form está vacío de datos
 * humanos (sin draft restaurado ni campos extra en `formData`, etc.) — así
 * no se pisa trabajo en curso del técnico. El draft en `localStorage` se
 * fusiona al state antes, vía efecto propio de CapturaPage.
 */
import { useCallback } from 'react';
import { getSupabase, isSupabaseConfigured } from '../client';
import { parseGestionNotasFromColumn, type GestionNotasParsed } from '../gestionNotasParse';
import { useCachedFetch } from './useCachedFetch';

export interface CapturaHydratePropietario {
  /** Plaintext seguro de mostrar (ej. "MARTINEZ G. L."). */
  titular_masked: string | null;
  tipo: 'ejidatario' | 'comunero' | 'posesionario' | 'propietario' | 'avecindado' | null;
  folio_derechos: string | null;
  folio_tierras: string | null;
  clave_catastral: string | null;
  /** Estos booleanos delatan si el campo cifrado tiene valor sin
   *  exponerlo. Útil para que la UI muestre "INE registrado" en vez
   *  de blanco confuso. */
  tiene_telefono: boolean;
  tiene_email: boolean;
}

export interface CapturaHydrateGestion {
  estado: string | null;
  firmado: boolean;
  riesgo: 'bajo' | 'medio' | 'alto' | null;
  plazo_anos: number | null;
  monto_renta_ha: number | null;
  /** Bloques parseados del JSON en `gestiones.notas`. Espejo de lo que
   *  CapturaPage escribe al hacer submit. */
  notas: GestionNotasParsed;
}

export interface CapturaHydrateIrregularidad {
  subtipo: string;
  gravedad: 'leve' | 'media' | 'alta' | 'bloqueante';
  descripcion: string | null;
  resuelta: boolean;
}

export interface CapturaHydrateData {
  /** Existe row en `parcelas` para el código público dado. Si false el
   *  resto viene null/empty. */
  parcela_existe: boolean;
  /** Titular principal (rol='titular' del link). Null si la parcela aún
   *  no tiene propietario asociado. */
  propietario: CapturaHydratePropietario | null;
  /** Una sola gestión por (tenant, parcela) — UNIQUE en schema. Null si
   *  el técnico aún no abrió la parcela. */
  gestion: CapturaHydrateGestion | null;
  /** Lista de irregularidades activas (resuelta=false). El form solo
   *  puede mostrar la primera mientras la UI no soporte multi-row;
   *  igual exponemos todas para histórico/debug. */
  irregularidades: CapturaHydrateIrregularidad[];
}

const EMPTY_DATA: CapturaHydrateData = {
  parcela_existe: false,
  propietario: null,
  gestion: null,
  irregularidades: [],
};

interface ParcelaRow {
  id: string;
  parcela_id: string;
  /** clave_catastral desde 0040 vive en parcelas (no en propietarios). */
  clave_catastral: string | null;
  /** parcela_propietario tiene desde 0040: tipo, folio_derechos, folio_tierras
   *  como atributos de la relación titular↔parcela. La misma persona puede
   *  tener tipo distinto en parcelas distintas. Las columnas legacy en
   *  propietarios todavía existen (no DROPped) pero la fuente de verdad
   *  para datos nuevos es parcela_propietario. */
  parcela_propietario: Array<{
    rol: string;
    tipo: string | null;
    folio_derechos: string | null;
    folio_tierras: string | null;
    propietarios: {
      titular_masked: string | null;
      /** Legacy fallback — solo se lee si parcela_propietario tiene valores
       *  null (capturas anteriores a 0040). */
      tipo: string | null;
      folio_derechos: string | null;
      folio_tierras: string | null;
      clave_catastral: string | null;
      tiene_telefono: boolean | null;
      tiene_email: boolean | null;
    } | null;
  }> | null;
  gestiones: Array<{
    estado: string | null;
    firmado: boolean | null;
    riesgo: string | null;
    plazo_anos: number | null;
    monto_renta_ha: number | null;
    notas: string | null;
  }> | null;
  irregularidades: Array<{
    subtipo: string;
    gravedad: string;
    descripcion: string | null;
    resuelta: boolean | null;
  }> | null;
}

const VALID_TIPO = new Set(['ejidatario', 'comunero', 'posesionario', 'propietario', 'avecindado']);
const VALID_RIESGO = new Set(['bajo', 'medio', 'alto']);
const VALID_GRAVEDAD = new Set(['leve', 'media', 'alta', 'bloqueante']);

/**
 * Hidrata el formulario de Captura para una parcela. Returns `EMPTY_DATA`
 * si Supabase no está configurado o el parcelaPublicId es vacío.
 */
export function useCapturaHydrate(parcelaPublicId: string | undefined): {
  data: CapturaHydrateData;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
} {
  const fetcher = useCallback(async (): Promise<CapturaHydrateData> => {
    if (!parcelaPublicId || !isSupabaseConfigured()) return EMPTY_DATA;
    const sb = getSupabase();
    // Una sola query con embeds: parcelas → parcela_propietario → propietarios,
    // parcelas → gestiones, parcelas → irregularidades.
    const { data: row, error: err } = await sb
      .from('parcelas')
      .select(`
        id,
        parcela_id,
        clave_catastral,
        parcela_propietario(
          rol,
          tipo,
          folio_derechos,
          folio_tierras,
          propietarios(
            titular_masked, tipo, folio_derechos, folio_tierras, clave_catastral,
            tiene_telefono, tiene_email
          )
        ),
        gestiones(estado, firmado, riesgo, plazo_anos, monto_renta_ha, notas),
        irregularidades(subtipo, gravedad, descripcion, resuelta)
      `)
      .eq('parcela_id', parcelaPublicId)
      .maybeSingle();
    if (err) throw err;
    if (!row) return EMPTY_DATA;
    const par = row as unknown as ParcelaRow;

    // Propietario titular (rol='titular') si existe.
    //
    // Fuente de verdad post-mig 0040:
    //   - tipo / folio_derechos / folio_tierras → parcela_propietario (link)
    //   - clave_catastral → parcelas (polígono)
    //   - titular_masked / tiene_telefono / tiene_email → propietarios (PII)
    //
    // Fallback legacy: si parcela_propietario.tipo/folio_* son NULL (capturas
    // pre-0040), leemos de propietarios. La migration backfill cubrió las 12
    // capturas existentes; los 183 propietarios solo en inventario_titulares
    // mantendrán sus folios en propietarios hasta primer captura via wizard.
    let propietario: CapturaHydratePropietario | null = null;
    const links = Array.isArray(par.parcela_propietario) ? par.parcela_propietario : [];
    const titularLink = links.find((l) => l.rol === 'titular') ?? links[0];
    const p = titularLink?.propietarios;
    if (p) {
      const linkTipo = titularLink?.tipo ?? null;
      const linkFolioD = titularLink?.folio_derechos ?? null;
      const linkFolioT = titularLink?.folio_tierras ?? null;
      // Preferir valores del link; fallback a propietarios legacy si link es NULL.
      const tipo = (linkTipo ?? p.tipo) as string | null;
      propietario = {
        titular_masked: p.titular_masked,
        tipo: tipo && VALID_TIPO.has(tipo)
          ? (tipo as CapturaHydratePropietario['tipo'])
          : null,
        folio_derechos: linkFolioD ?? p.folio_derechos,
        folio_tierras: linkFolioT ?? p.folio_tierras,
        // clave_catastral pertenece a la parcela (no al propietario) desde 0040.
        // Fallback legacy: propietarios.clave_catastral si parcelas no la tiene.
        clave_catastral: par.clave_catastral ?? p.clave_catastral,
        tiene_telefono: !!p.tiene_telefono,
        tiene_email: !!p.tiene_email,
      };
    } else if (par.clave_catastral) {
      // Caso especial: la parcela tiene clave_catastral pero aún no hay
      // titular vinculado. Devolvemos un "ghost" propietario solo con la
      // clave para que el form la prefille (UX consistente).
      propietario = {
        titular_masked: null,
        tipo: null,
        folio_derechos: null,
        folio_tierras: null,
        clave_catastral: par.clave_catastral,
        tiene_telefono: false,
        tiene_email: false,
      };
    }

    // Gestión: hay UNIQUE (tenant, parcela) en el schema → 0 o 1 fila.
    let gestion: CapturaHydrateGestion | null = null;
    const gList = Array.isArray(par.gestiones) ? par.gestiones : [];
    const g = gList[0];
    if (g) {
      const parsed = parseGestionNotasFromColumn(g.notas);
      const notas: GestionNotasParsed = {
        has_gestion: true,
        notas_text: parsed.fallbackText,
        contrato: parsed.contrato,
        gestion: parsed.gestion,
        campo: parsed.campo,
        narrativa: parsed.narrativa,
        riesgo: null,
        estado: null,
        firmado: false,
        plazo_anos: null,
        monto_renta_ha: null,
        bono_firma_mxn: parsed.bono_firma_mxn,
        construccion: parsed.construccion,
        modelo_negocio_total_mxn: parsed.modelo_negocio_total_mxn,
      };
      // Sincronizamos los campos columnares (no del JSON) en notas
      // para que el consumidor pueda leer todo desde un solo lugar.
      notas.estado = g.estado;
      notas.firmado = !!g.firmado;
      notas.plazo_anos = g.plazo_anos;
      notas.monto_renta_ha = g.monto_renta_ha;
      notas.riesgo = g.riesgo && VALID_RIESGO.has(g.riesgo)
        ? (g.riesgo as 'bajo' | 'medio' | 'alto')
        : null;
      gestion = {
        estado: g.estado,
        firmado: !!g.firmado,
        riesgo: notas.riesgo,
        plazo_anos: g.plazo_anos,
        monto_renta_ha: g.monto_renta_ha,
        notas,
      };
    }

    const irrRows = Array.isArray(par.irregularidades) ? par.irregularidades : [];
    const irregularidades: CapturaHydrateIrregularidad[] = irrRows
      .filter((r) => VALID_GRAVEDAD.has(r.gravedad) && !r.resuelta)
      .map((r) => ({
        subtipo: r.subtipo,
        gravedad: r.gravedad as CapturaHydrateIrregularidad['gravedad'],
        descripcion: r.descripcion ?? null,
        resuelta: !!r.resuelta,
      }));

    return {
      parcela_existe: true,
      propietario,
      gestion,
      irregularidades,
    };
  }, [parcelaPublicId]);

  const cacheKey = parcelaPublicId ? `captura_hydrate:${parcelaPublicId}` : 'captura_hydrate:none';
  const { data, loading, error, refetch } = useCachedFetch<CapturaHydrateData>(
    cacheKey,
    fetcher,
    { ttlMs: 30_000 },
  );
  return { data: data ?? EMPTY_DATA, loading, error, refetch };
}
